/**
 * f12.3: the Lock dialog, after `#lockModal` in `review.html` (design D8).
 *
 * Every figure, the blockers and `can_freeze` come from the server's preview;
 * nothing here decides whether a cycle may be locked. The POST sends the
 * preview's version, so a `409` (whose refetch brings a fresh preview) leaves
 * the dialog ready to try again against what the server holds now.
 */

import { useState } from 'react';
import { Ban, Info, Lock } from 'lucide-react';

import type { Cycle, FreezePreview } from '@/api/client';
import { Modal } from '@/routes/reference-data/Modal';

import { EMPTY, formatMoney } from '../review/reviewFormat';
import { useCycle } from '@/api/queries';

import { freezeFailure, useFreezeCycle, useFreezePreview } from './useFreeze';

const COUNT = new Intl.NumberFormat('en-US');

/** True when the server offers this caller the lock (Finance only). */
export function canLock(cycle: Cycle): boolean {
  return cycle.available_transitions.some((edge) => edge.action === 'FREEZE');
}

function PreviewBody({ preview }: { preview: FreezePreview }) {
  const warnings = preview.validation_warning_count;
  const pendingVariances = preview.pending_variance_count ?? 0;
  return (
    <>
      <dl className="dl cols-2 lock-figures">
        <div>
          <dt>Clients in scope</dt>
          <dd>{COUNT.format(preview.client_count)}</dd>
        </div>
        <div>
          <dt>Forecast lines</dt>
          <dd>{COUNT.format(preview.line_count)}</dd>
        </div>
        <div>
          <dt>Committed FY (USD)</dt>
          <dd className="mono">{formatMoney(preview.usd_total)}</dd>
        </div>
        <div>
          <dt>USD rate frozen at</dt>
          <dd className="mono">
            {preview.usd_rate_to_eur ? `${preview.usd_rate_to_eur} EUR/USD` : EMPTY}
          </dd>
        </div>
      </dl>

      {warnings > 0 && (
        <div className="alert alert-info lock-row" role="status">
          <Info aria-hidden="true" />
          <div>
            <div className="a-title">
              {COUNT.format(warnings)} validation warning{warnings === 1 ? '' : 's'}
            </div>
            <div className="a-body">Warnings never block the lock; they stay visible on the review.</div>
          </div>
        </div>
      )}

      {/* f14.2 (Q-f14.2-9): pending reconciliation variances warn, never block. */}
      {pendingVariances > 0 && (
        <div className="alert alert-warning lock-row" role="status" data-warning="pending-variances">
          <Info aria-hidden="true" />
          <div>
            <div className="a-title">
              {COUNT.format(pendingVariances)} reconciliation variance{pendingVariances === 1 ? '' : 's'} pending
            </div>
            <div className="a-body">They stay read-only on the frozen cycle until it is reopened.</div>
          </div>
        </div>
      )}

      {preview.blockers.map((blocker) => (
        <div key={blocker.code} className="alert alert-danger lock-row" role="alert" data-blocker={blocker.code}>
          <Ban aria-hidden="true" />
          <div>
            <div className="a-body">{blocker.message}</div>
          </div>
        </div>
      ))}
    </>
  );
}

export function LockCycleDialog({ cycle, onClose }: { cycle: Cycle; onClose: () => void }) {
  const preview = useFreezePreview(cycle.id, true);
  const freeze = useFreezeCycle(cycle.id);
  const data = preview.data;
  const firstBlocker = data?.blockers[0]?.message;

  return (
    <Modal
      title={`Lock cycle ${data?.code ?? cycle.code}?`}
      subtitle={`Finance only · ${data?.label ?? cycle.derived.label}`}
      onClose={onClose}
      onSubmit={() => {
        if (data) freeze.mutate(data.version, { onSuccess: onClose });
      }}
      busy={freeze.isPending}
      submitDisabled={!data || !data.can_freeze || preview.isFetching}
      submitTitle={data && !data.can_freeze ? firstBlocker : undefined}
      submitLabel="Lock and snapshot"
      submitIcon={<Lock aria-hidden="true" />}
      layout="plain"
      message={freezeFailure(freeze.error) ?? freezeFailure(preview.error)}
    >
      <div className="alert alert-warning mb-4">
        <Lock aria-hidden="true" />
        <div>
          <div className="a-title">Locking takes an immutable snapshot</div>
          <div className="a-body">
            All input stops, the figures become the {data?.label ?? cycle.derived.label} baseline, and
            the FX rate in force is frozen with them. Reopening later requires a written reason.
          </div>
        </div>
      </div>
      {data ? <PreviewBody preview={data} /> : !preview.error && <p className="hint">Reading the figures…</p>}
    </Modal>
  );
}

/** The page-head "Lock cycle" button; rendered only when the server offers FREEZE. */
export function LockCycleButton({ cycle }: { cycle: Cycle }) {
  const [open, setOpen] = useState(false);
  if (!canLock(cycle)) return null;
  return (
    <>
      <button type="button" className="btn btn-primary" onClick={() => setOpen(true)}>
        <Lock aria-hidden="true" /> Lock cycle
      </button>
      {open && <LockCycleDialog cycle={cycle} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * The lock on the f12.1 review. The page renders it only when the review's
 * server-computed `can_lock` is true (In review, Finance), so the cycle -- a
 * Finance/Admin read -- is fetched only for a caller the route admits. The
 * button itself still requires the cycle's FREEZE transition.
 */
export function CycleLockAction({ cycleId }: { cycleId: string }) {
  const cycle = useCycle(cycleId);
  return cycle.data ? <LockCycleButton cycle={cycle.data} /> : null;
}
